import { Component, Prop, State, Host, h, Fragment } from '@stencil/core';

type Schedule = {
  date: number;
  video: string | undefined;
}

const endpoint = "https://script.google.com/macros/s/AKfycbwBQxgvGDJ8inYyPhvEekBxYPJYDSYVSCSja4QIi3J_LsM2bjbACk6sWqcGwN0aU0dS/exec";

@Component({
  tag: 'chapel-video',
  styleUrl: 'chapel-video.scss',
  shadow: true,
})
export class ChapelVideo {

  @Prop() bg: string | undefined;
  @Prop() fallbackBg: string | undefined;
  @Prop() fallbackText: string | undefined;
  @Prop() autoplay = false;
  @Prop() controls = false;

  @State() isLoading = false;
  @State() schedule: Schedule[] = [];

  componentDidLoad(): void {
    this.isLoading = true;
    const url =`${endpoint}?sheet=video-schedule`;
    fetch(url).then(results => results.json()).then(({ data }) => {
      this.schedule = data.map(({ date, videoId }) => ({
        videoId,
        date: new Date(date).getTime(),
      })).sort();
      this.isLoading = false;
    });
  }

  render() {
    const src = this.getVideoUrl(this.schedule);
    const noVideo = !this.isLoading && !src;

    return (
      <Host>
          {noVideo && this.renderFallback()}
          {!noVideo && this.renderBackground()}
          {!noVideo && (
            <iframe
              allowFullScreen
              src={src}
              frameBorder="0"
              allow="
                accelerometer;
                autoplay;
                clipboard-write;
                encrypted-media;
                gyroscope;
                picture-in-picture;
                web-share;
              "
            ></iframe>
          )}
      </Host>
    );
  }

  renderBackground() {
    const { renderSpinner, bg, isLoading } = this;
    return (
      <div class="bg">
        {isLoading && <img src={bg} alt="Loading..." />}
        {isLoading && renderSpinner()}
      </div>
    );
  }

  renderFallback() {
    const { fallbackBg, fallbackText } = this;
    return (
      <Fragment>
        <div class="bg">
          <img src={fallbackBg} alt={fallbackText} />
        </div>
        {fallbackText && <h1 class="fallback">{fallbackText}</h1>}
      </Fragment>
    );
  }

  // https://loading.io/spinner/ring/-donut-pie-ring-rotate
  renderSpinner() {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" xmlns-xlink="http://www.w3.org/1999/xlink" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid">
        <circle cx="50" cy="50" r="30" stroke="#ffffff" stroke-width="6" fill="none" opacity="0.2"></circle>
        <circle cx="50" cy="50" r="30" stroke="#ffffff" stroke-width="6" stroke-linecap="round" fill="none">
          <animateTransform attributeName="transform" type="rotate" repeatCount="indefinite" dur="1s" values="0 50 50;180 50 50;720 50 50" keyTimes="0;0.5;1"></animateTransform>
          <animate attributeName="stroke-dasharray" repeatCount="indefinite" dur="1s" values="18.84955592153876 169.64600329384882;94.2477796076938 94.24777960769377;18.84955592153876 169.64600329384882" keyTimes="0;0.5;1"></animate>
        </circle>
      </svg>
    );
  }

  getVideoUrl(schedule: Schedule[]): string {
    const { autoplay, controls } = this;

    const params = new URLSearchParams({
      'rel': '0',
      'modestbranding': '1',
      'mute': autoplay ? '1' : '0',
      'autoplay': autoplay ? '1' : '0',
      'controls': controls ? '1' : '0',
    });

    const now = Date.now();

    const next = schedule.find(({ date: dateOfCurrent }, index) => {
      const dateOfNext = schedule[index + 1]?.date ?? Infinity;
      return dateOfCurrent < now && now < dateOfNext;
    });

    const videoId = getVideoId(next?.video?.trim());

    return videoId ? `https://www.youtube.com/embed/${videoId}?${params}`: undefined;
  }

}

function getVideoId(url = ''): string {
  const [, videoId] = url.match(/\/live\/([a-zA-Z0-9_-]{11})/) ?? [];
  return videoId ?? url;
}
